<script setup>
import { formatNumber, formatPercent } from '../Utils/numberFormat.js'
import { computed, reactive, ref, watch } from 'vue'
import axios from 'axios'
import StatChart from './StatChart.vue'
import ChartSkeleton from './ChartSkeleton.vue'
import { useHttpErrorsHandler } from '../Composables/httpErrorsHandler.js'

const props = defineProps({
    centerId: { type: Number, default: null },
    from: { type: String, default: null },
    to: { type: String, default: null },
})

const { httpErrorsHandler } = useHttpErrorsHandler()

const COLORS = { answered: '#0ab39c', missed: '#f06548', voicemail: '#f7b84b', outgoing: '#3577f1', neutral: '#878a99' }

const ANSWER_HELP = "Appels entrants décrochés sur l'ensemble des appels entrants pendant les horaires d'ouverture (décrochés, manqués, messagerie). Un client qui rappelle compte pour un nouvel appel. Les appels arrivés pendant la fermeture sont comptés à part."
const INCOMING_HELP = "Appels reçus pendant les horaires d'ouverture : décrochés, manqués et messagerie. Les appels raccrochés en moins de 4 secondes (faux numéro, raccroché dès l'annonce) ne sont pas comptés."
const OUT_OF_HOURS_HELP = "Appels arrivés pendant la fermeture du centre (scénario « Fermé » du standard RingOver) : personne ne pouvait décrocher, ils ne pèsent pas sur le taux de décroché mais restent à rappeler."
const CALLBACK_HELP = "Pour chaque appel manqué (plusieurs tentatives d'un client non joint comptent pour une) ou message laissé : le client a-t-il été joint dans les 30 jours, par n'importe quel centre ? Soit un centre l'a rappelé et il a décroché, soit il a rappelé lui-même et a été pris. Un rappel resté sans réponse ne compte pas. Tant qu'un manqué a moins de 30 jours, il est « en attente » : le chiffre du mois en cours est provisoire."
const TALK_HELP = 'Durée moyenne entre le décroché et le raccroché, appels entrants et sortants aboutis.'
const HEATMAP_HELP = "Appels entrants par jour et par heure sur la période. En mode « non décrochés », la couleur indique la part d'appels manqués ou tombés sur la messagerie : les créneaux où renforcer l'accueil téléphonique."

const loading = ref(true)
const stats = ref(null)

const formatSeconds = (seconds) => {
    if (seconds === null || seconds === undefined) return '—'
    const minutes = Math.floor(seconds / 60)
    return minutes > 0 ? `${minutes} min ${String(seconds % 60).padStart(2, '0')} s` : `${seconds} s`
}
const formatDelay = (minutes) => {
    if (minutes === null || minutes === undefined) return '—'
    if (minutes < 60) return `${minutes} min`
    const hours = minutes / 60
    return hours < 48 ? `${Math.round(hours)} h` : `${Math.round(hours / 24)} j`
}

// Filtres changés coup sur coup (période puis centre) : seule la dernière réponse s'affiche, une
// réponse plus lente ne vient pas écraser le résultat du dernier filtre
let latestRequest = 0

const fetchStats = () => {
    const request = ++latestRequest
    loading.value = true
    const params = {}
    if (props.centerId) params.center_id = props.centerId
    if (props.from) params.from = props.from
    if (props.to) params.to = props.to

    axios.get('/api/call-statistics', { params })
        .then((response) => {
            if (request === latestRequest) stats.value = response.data.data
        })
        .catch((error) => {
            if (request === latestRequest) httpErrorsHandler(error)
        })
        .finally(() => {
            if (request === latestRequest) loading.value = false
        })
}

watch(() => [props.centerId, props.from, props.to], fetchStats, { immediate: true })

// Fraîcheur des données : le calcul est mis en cache 5 min côté serveur
const computedAtLabel = computed(() => {
    if (!stats.value?.computed_at) return ''
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(new Date(stats.value.computed_at)).replace(':', ' h ')
})

const totals = computed(() => stats.value?.totals ?? {})
const callbacks = computed(() => stats.value?.callbacks ?? {})

// --- Évolution mensuelle ---
const monthLabel = (period) => {
    const [year, month] = period.split('-').map(Number)
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(new Date(year, month - 1, 1))
    return `${label} ${String(year).slice(2)}`
}

const monthlyChart = computed(() => {
    const rows = stats.value?.monthly ?? []
    return {
        series: [
            { name: 'Décrochés', type: 'column', data: rows.map((row) => row.answered) },
            { name: 'Manqués', type: 'column', data: rows.map((row) => row.missed) },
            { name: 'Messagerie', type: 'column', data: rows.map((row) => row.voicemail) },
            { name: 'Hors horaires', type: 'column', data: rows.map((row) => row.out_of_hours ?? 0) },
            { name: 'Sortants', type: 'line', data: rows.map((row) => row.outgoing) },
        ],
        options: {
            chart: { stacked: true, toolbar: { show: false }, zoom: { enabled: false } },
            colors: [COLORS.answered, COLORS.missed, COLORS.voicemail, COLORS.neutral, COLORS.outgoing],
            plotOptions: { bar: { columnWidth: '45%', borderRadius: 3 } },
            stroke: { width: [0, 0, 0, 0, 3], curve: 'smooth' },
            dataLabels: { enabled: false },
            xaxis: { categories: rows.map((row) => monthLabel(row.period)) },
            yaxis: { labels: { formatter: (val) => formatNumber(Math.round(val)) } },
            legend: { position: 'bottom' },
            grid: { borderColor: '#f1f1f1' },
            tooltip: { shared: true, intersect: false, y: { formatter: (val) => formatNumber(val) } },
            noData: { text: 'Aucune donnée sur la période' },
        },
    }
})

// --- Suite donnée aux appels manqués (30 jours) ---
const callbackBuckets = computed(() => [
    { key: 'within_hour', label: "Rappelés en moins d'1 h", color: '#0ab39c' },
    { key: 'within_day', label: 'Rappelés sous 24 h', color: '#3577f1' },
    { key: 'within_week', label: 'Rappelés sous 7 jours', color: '#299cdb' },
    { key: 'within_month', label: 'Rappelés sous 30 jours', color: '#8fb8e8' },
    { key: 'client_reached', label: 'Le client a rappelé et a été pris', color: '#f7b84b' },
    { key: 'not_reached', label: 'Client non joint', color: '#f06548' },
    { key: 'pending', label: 'En attente (moins de 30 jours)', color: '#ced4da' },
].map((bucket) => ({
    ...bucket,
    count: callbacks.value[bucket.key] ?? 0,
    share: callbacks.value.episodes ? Math.round((callbacks.value[bucket.key] ?? 0) / callbacks.value.episodes * 1000) / 10 : 0,
})))

const callbackChart = computed(() => ({
    series: callbackBuckets.value.map((bucket) => bucket.count),
    options: {
        chart: { type: 'donut' },
        labels: callbackBuckets.value.map((bucket) => bucket.label),
        colors: callbackBuckets.value.map((bucket) => bucket.color),
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: { width: 2 },
        plotOptions: {
            pie: {
                donut: {
                    size: '72%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Clients joints', formatter: () => formatPercent(callbacks.value.reached_rate) },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (val) => formatNumber(val) } },
    },
}))

// --- Classement des centres ---
const sort = reactive({ key: 'incoming', direction: 'desc' })

const centerRows = computed(() => (stats.value?.centers ?? []).map((center) => ({
    ...center,
    missed_total: center.missed + center.voicemail,
    callback_rate: center.callbacks?.callback_rate ?? null,
    reached_rate: center.callbacks?.reached_rate ?? null,
    callback_delay: center.callbacks?.avg_delay_minutes ?? null,
    unreached: center.callbacks?.unreached ?? 0,
})))

const sortedCenters = computed(() => [...centerRows.value].sort((a, b) => {
    const left = a[sort.key] ?? -1
    const right = b[sort.key] ?? -1
    const result = typeof left === 'string' ? left.localeCompare(right) : left - right
    return sort.direction === 'asc' ? result : -result
}))

const setSort = (key) => {
    if (sort.key === key) {
        sort.direction = sort.direction === 'asc' ? 'desc' : 'asc'
    } else {
        sort.key = key
        sort.direction = key === 'center_name' ? 'asc' : 'desc'
    }
}
const sortIcon = (key) => (sort.key !== key ? 'mdi-unfold-more-horizontal text-muted' : (sort.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'))

// Seuils de couleur [vert à partir de, orange à partir de], identiques sur tous les écrans d'appels
const ANSWER_RATE_TARGETS = [70, 50]
const REACHED_RATE_TARGETS = [90, 75]

const rateClass = (rate, good, average) => {
    if (rate === null || rate === undefined) return 'text-muted'
    if (rate >= good) return 'bg-success-subtle text-success'
    if (rate >= average) return 'bg-warning-subtle text-warning'
    return 'bg-danger-subtle text-danger'
}

// --- Affluence (jour × heure) ---
const heatmapMode = ref('incoming')
const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

const heatmapChart = computed(() => {
    const cells = stats.value?.heatmap ?? []
    // Quelques appels isolés la nuit étireraient l'axe : on garde les heures qui pèsent vraiment
    const peak = Math.max(0, ...cells.map((cell) => cell.incoming))
    const hours = cells.filter((cell) => cell.incoming >= peak * 0.01).map((cell) => cell.hour)
    const firstHour = Math.min(8, ...hours)
    const lastHour = Math.max(19, ...hours)
    const days = [1, 2, 3, 4, 5, 6, 7].filter((day) => day <= 6 || cells.some((cell) => cell.weekday === 7))

    const series = days.slice().reverse().map((day) => ({
        name: WEEKDAYS[day - 1],
        data: Array.from({ length: lastHour - firstHour + 1 }, (_, index) => {
            const hour = firstHour + index
            const cell = cells.find((item) => item.weekday === day && item.hour === hour)
            const value = heatmapMode.value === 'incoming'
                ? (cell?.incoming ?? 0)
                : (cell?.incoming ? Math.round(cell.unanswered / cell.incoming * 100) : 0)
            return { x: `${hour} h`, y: value, incoming: cell?.incoming ?? 0, unanswered: cell?.unanswered ?? 0 }
        }),
    }))

    return {
        series,
        options: {
            chart: { type: 'heatmap', toolbar: { show: false } },
            colors: [heatmapMode.value === 'incoming' ? '#3577f1' : '#f06548'],
            dataLabels: { enabled: false },
            plotOptions: { heatmap: { radius: 3, shadeIntensity: 0.6 } },
            stroke: { width: 2, colors: ['#fff'] },
            legend: { show: false },
            tooltip: {
                custom: ({ seriesIndex, dataPointIndex, w }) => {
                    const point = w.config.series[seriesIndex].data[dataPointIndex]
                    const rate = point.incoming ? Math.round(point.unanswered / point.incoming * 100) : 0
                    return `<div class="px-2 py-1 small"><strong>${w.config.series[seriesIndex].name} ${point.x}</strong><br>${formatNumber(point.incoming)} appels · ${rate} % non décrochés</div>`
                },
            },
        },
    }
})
</script>

<template>
    <div>
        <p class="text-muted fs-12 mb-3" v-if="computedAtLabel">
            <i class="mdi mdi-refresh align-middle me-1"></i>
            Données d'appels calculées à {{ computedAtLabel }} · actualisées toutes les 5 minutes
        </p>

        <!-- KPI -->
        <div class="row">
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Appels entrants <span class="fs-12 fw-normal">(heures d'ouverture)</span>&nbsp;<i class="mdi mdi-information-outline" v-tooltip="INCOMING_HELP"></i></p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatNumber(totals.incoming) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading">
                                    <span class="text-danger">{{ formatNumber(totals.missed) }} manqués</span> ·
                                    <span class="text-warning">{{ formatNumber(totals.voicemail) }} messagerie</span>
                                </p>
                                <p class="text-muted fs-12 mb-0" v-if="!loading && totals.out_of_hours">
                                    + {{ formatNumber(totals.out_of_hours) }} hors horaires, comptés à part&nbsp;<i class="mdi mdi-information-outline" v-tooltip="OUT_OF_HOURS_HELP"></i>
                                </p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-success-subtle text-success rounded-circle fs-2"><i class="mdi mdi-phone-incoming"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Taux de décroché&nbsp;<i class="mdi mdi-information-outline" v-tooltip="ANSWER_HELP"></i></p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatPercent(totals.answer_rate) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading">{{ formatNumber(totals.answered) }} appels décrochés</p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-secondary-subtle text-secondary rounded-circle fs-2"><i class="mdi mdi-phone-check"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Clients joints après un manqué&nbsp;<i class="mdi mdi-information-outline" v-tooltip="CALLBACK_HELP"></i>
                                    <span v-if="!loading && callbacks.is_provisional" class="badge bg-light text-muted fw-normal ms-1" v-tooltip="`${formatNumber(callbacks.pending)} manqués ont encore jusqu'à 30 jours pour être rappelés`">provisoire</span>
                                </p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatPercent(callbacks.reached_rate) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading">{{ formatPercent(callbacks.callback_rate) }} rappelés · {{ formatNumber(callbacks.unreached) }} non joints<template v-if="callbacks.pending"> (dont {{ formatNumber(callbacks.pending) }} en attente)</template></p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-2"><i class="mdi mdi-phone-return"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Appels sortants</p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatNumber(totals.outgoing) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading">
                                    {{ formatNumber(totals.outgoing_unanswered) }} sans réponse ·
                                    conversation moy. {{ formatSeconds(totals.avg_talk_seconds) }}&nbsp;<i class="mdi mdi-information-outline" v-tooltip="TALK_HELP"></i>
                                </p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-info-subtle text-info rounded-circle fs-2"><i class="mdi mdi-phone-outgoing"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Évolution + rappel des manqués -->
        <div class="row">
            <div class="col-xl-8">
                <div class="card card-height-100">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">Appels par mois</h4>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="loading" variant="vertical" :height="340" />
                        <StatChart v-else type="line" :height="340" :series="monthlyChart.series" :options="monthlyChart.options" />
                    </div>
                </div>
            </div>
            <div class="col-xl-4">
                <div class="card card-height-100">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">Suite donnée aux appels manqués <span class="fs-12 fw-normal text-muted">(sous 30 jours)</span></h4>
                        <i class="mdi mdi-information-outline text-muted fs-16" v-tooltip="CALLBACK_HELP"></i>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="loading" variant="horizontal" :height="340" />
                        <template v-else-if="callbacks.episodes">
                            <StatChart type="donut" :height="210" :series="callbackChart.series" :options="callbackChart.options" />
                            <ul class="list-unstyled mb-0 mt-3">
                                <li v-for="bucket in callbackBuckets" :key="bucket.key" class="d-flex align-items-center py-1 fs-13">
                                    <span class="rounded-circle me-2 flex-shrink-0" :style="{ width: '10px', height: '10px', background: bucket.color }"></span>
                                    <span class="flex-grow-1">{{ bucket.label }}</span>
                                    <span class="fw-semibold me-2">{{ formatNumber(bucket.count) }}</span>
                                    <span class="text-muted" style="min-width: 48px; text-align: right;">{{ formatPercent(bucket.share) }}</span>
                                </li>
                            </ul>
                        </template>
                        <div v-else class="text-center text-muted py-5">Aucun appel manqué sur la période</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Classement des centres -->
        <div class="card">
            <div class="card-header align-items-center d-flex">
                <h4 class="card-title mb-0 flex-grow-1">Appels par centre</h4>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="loading" variant="horizontal" :height="300" />
                <div v-else class="table-responsive table-card" style="max-height: 480px;">
                    <table class="table table-borderless table-hover table-nowrap align-middle mb-0">
                        <thead class="table-light position-sticky top-0">
                            <tr class="text-muted">
                                <th role="button" class="user-select-none" @click="setSort('center_name')">Centre <i class="mdi align-middle" :class="sortIcon('center_name')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('incoming')">Entrants <i class="mdi align-middle" :class="sortIcon('incoming')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('answer_rate')">Taux de décroché <i class="mdi align-middle" :class="sortIcon('answer_rate')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('missed_total')">Non décrochés <i class="mdi align-middle" :class="sortIcon('missed_total')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('out_of_hours')" v-tooltip="OUT_OF_HOURS_HELP">Hors horaires <i class="mdi align-middle" :class="sortIcon('out_of_hours')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('reached_rate')">Clients joints <i class="mdi align-middle" :class="sortIcon('reached_rate')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('callback_rate')">Rappelés <i class="mdi align-middle" :class="sortIcon('callback_rate')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('callback_delay')">Délai moyen de rappel <i class="mdi align-middle" :class="sortIcon('callback_delay')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('unreached')">Clients non joints <i class="mdi align-middle" :class="sortIcon('unreached')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('outgoing')">Sortants <i class="mdi align-middle" :class="sortIcon('outgoing')"></i></th>
                                <th role="button" class="text-center user-select-none" @click="setSort('avg_talk_seconds')">Conversation moy. <i class="mdi align-middle" :class="sortIcon('avg_talk_seconds')"></i></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="center in sortedCenters" :key="center.center_id">
                                <td class="fw-medium">{{ center.center_name }}</td>
                                <td class="text-center">{{ formatNumber(center.incoming) }}</td>
                                <td class="text-center"><span class="badge fs-12" :class="rateClass(center.answer_rate, ...ANSWER_RATE_TARGETS)">{{ formatPercent(center.answer_rate) }}</span></td>
                                <td class="text-center">{{ formatNumber(center.missed_total) }}</td>
                                <td class="text-center text-muted">{{ formatNumber(center.out_of_hours) }}</td>
                                <td class="text-center"><span class="badge fs-12" :class="rateClass(center.reached_rate, ...REACHED_RATE_TARGETS)">{{ formatPercent(center.reached_rate) }}</span></td>
                                <td class="text-center">{{ formatPercent(center.callback_rate) }}</td>
                                <td class="text-center">{{ formatDelay(center.callback_delay) }}</td>
                                <td class="text-center" :class="{ 'text-danger fw-semibold': center.unreached > 0 }">{{ formatNumber(center.unreached) }}</td>
                                <td class="text-center">{{ formatNumber(center.outgoing) }}</td>
                                <td class="text-center">{{ formatSeconds(center.avg_talk_seconds) }}</td>
                            </tr>
                            <tr v-if="sortedCenters.length === 0">
                                <td colspan="11" class="text-center text-muted py-4">Aucune donnée sur la période</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Affluence -->
        <div class="card">
            <div class="card-header align-items-center d-flex flex-wrap gap-2">
                <h4 class="card-title mb-0 flex-grow-1">
                    Affluence par jour et par heure&nbsp;<i class="mdi mdi-information-outline text-muted" v-tooltip="HEATMAP_HELP"></i>
                </h4>
                <div class="btn-group btn-group-sm flex-shrink-0" role="group">
                    <button type="button" class="btn shadow-none" :class="heatmapMode === 'incoming' ? 'btn-secondary' : 'btn-soft-secondary'" @click="heatmapMode = 'incoming'">Appels entrants</button>
                    <button type="button" class="btn shadow-none" :class="heatmapMode === 'unanswered' ? 'btn-secondary' : 'btn-soft-secondary'" @click="heatmapMode = 'unanswered'">% non décrochés</button>
                </div>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="loading" variant="vertical" :height="320" />
                <StatChart v-else type="heatmap" :height="320" :series="heatmapChart.series" :options="heatmapChart.options" />
            </div>
        </div>
    </div>
</template>
